"use client";

import React, { useMemo } from "react";
import { Building2 } from "lucide-react";
import { CompanyPopover } from "@/features/companies/components/CompanyPopover";
import { useCompany } from "@/lib/hooks/api/companies";
import { Company } from "@/lib/hooks/api/companies";
import type {
  SmartCompanyDisplayProps,
  SmartCompanyChipProps,
} from "@/components/shared/UniversalCRMTable/types/shared";

export function SmartCompanyDisplay({
  companyId,
  companyData,
  fallbackText = "Unknown",
  showIcon = true,
  className = "",
  disabled = false,
  showPopover = true,
}: SmartCompanyDisplayProps & { disabled?: boolean }) {
  // Only fetch company if we don't have companyData and need to lookup
  const { data: company, isLoading } = useCompany(companyId, { enabled: !companyData });

  // Memoize company lookup to prevent unnecessary re-renders
  const companyInfo = useMemo(() => {
    return companyData || company;
  }, [companyData, company]);

  // Memoize display name calculation
  const displayName = useMemo(() => {
    if (!companyInfo) return fallbackText;
    return companyInfo.name || "Unknown Company";
  }, [companyInfo, fallbackText]);

  // Show loading state if we're fetching and don't have data
  if (!companyInfo && isLoading && !companyData) {
    return <span className={`text-gray-400 ${className}`}>Loading...</span>;
  }

  if (!companyInfo) {
    return <span className={`text-gray-400 ${className}`}>{fallbackText}</span>;
  }

  // If disabled, just show the text without popover
  if (disabled) {
    return (
      <div className={`flex items-center gap-1 rounded px-0.5 ${className}`}>
        {showIcon && <Building2 className="h-3 w-3 text-gray-400 flex-shrink-0" />}
        <span className="text-xs text-card-foreground truncate">{displayName}</span>
      </div>
    );
  }

  // Inline detail/edit surfaces pass showPopover={false} so the parent row's
  // click-to-edit handler runs (CompanySelect) instead of opening CompanyPopover.
  if (!showPopover) {
    return (
      <div className={`flex items-center gap-1 rounded px-0.5 ${className}`}>
        {showIcon && <Building2 className="h-3 w-3 text-gray-400 flex-shrink-0" />}
        <span className="text-xs text-card-foreground truncate">{displayName}</span>
      </div>
    );
  }

  return (
    <CompanyPopover name={companyInfo.name || ""} companyId={companyInfo.id}>
      <div
        className={`flex items-center gap-1 cursor-pointer hover:bg-card-50 rounded px-0.5 ${className}`}
      >
        {showIcon && <Building2 className="h-3 w-3 text-gray-400 flex-shrink-0" />}
        <span className="text-xs text-card-foreground hover:text-blue-600 truncate">{displayName}</span>
      </div>
    </CompanyPopover>
  );
}

export function SmartCompanyChip({
  companyId,
  companyData,
  fallbackText = "Unknown",
  size = "sm",
  showPopover = true,
}: SmartCompanyChipProps) {
  const { data: company, isLoading } = useCompany(companyId, { enabled: !companyData });

  // Memoize company lookup to prevent unnecessary re-renders
  const companyInfo = useMemo(() => {
    return companyData || company;
  }, [companyData, company]);

  // Memoize display name and initials calculation
  const { displayName, initials } = useMemo(() => {
    if (!companyInfo) return { displayName: fallbackText, initials: fallbackText.slice(0, 2) };

    const name = companyInfo.name || "Unknown Company";
    const companyInitials = name
      .split(" ")
      .map((n: string) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

    return { displayName: name, initials: companyInitials };
  }, [companyInfo, fallbackText]);

  // Show loading state if we're fetching and don't have data
  if (!companyInfo && isLoading && !companyData) {
    return (
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-card-100 text-gray-600 dark:text-gray-400"
        }
      >
        Loading...
      </span>
    );
  }

  if (!companyInfo) {
    return (
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-card-100 text-gray-600 dark:text-gray-400"
        }
      >
        {fallbackText}
      </span>
    );
  }

  if (!showPopover) {
    return (
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
        }
      >
        {size === "sm" ? initials : displayName}
      </span>
    );
  }

  return (
    <CompanyPopover name={companyInfo.name || ""} companyId={companyInfo.id}>
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:hover:bg-blue-900/50"
        }
      >
        {size === "sm" ? initials : displayName}
      </span>
    </CompanyPopover>
  );
}
